import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../service/auth.service';
import { WebTokenBridge } from '../../web-auth/web-token.bridge';
import { KeyUnlockDialogComponent } from './key-unlock-dialog.component';

/**
 * KX-017: Kısıtlı mod global banner'ı. Kullanıcının geçerli token'ı VAR ama private key'i YOK ise
 * (key-file'sız login veya key-file'sız restore edilmiş oturum) görünür. Şifreli verinin bu oturumda
 * kilitli olduğunu açıklar ve iki aksiyon sunar:
 *   - Anahtar Yükle  -> oturum-içi KeyUnlockDialog (Faz 2, re-login yok)
 *   - Anahtar Üret/İndir -> /info/generatekey
 *
 * Görünürlük AuthService.privateKeyPresent$ ile reaktiftir: anahtar yüklenince banner anında kaybolur.
 */
@Component({
    selector: 'app-restricted-banner',
    standalone: true,
    imports: [CommonModule, ButtonModule, TranslateModule, KeyUnlockDialogComponent],
    template: `
        <div *ngIf="showBanner()" class="restricted-banner">
            <div class="restricted-banner-message">
                <i class="pi pi-lock"></i>
                <span>{{ 'COMMON.RESTRICTED.BANNER_TEXT' | translate }}</span>
            </div>
            <div class="restricted-banner-actions">
                <p-button
                    [label]="'COMMON.RESTRICTED.LOAD_KEY' | translate"
                    icon="pi pi-upload"
                    size="small"
                    (onClick)="openUnlockDialog()"
                />
                <p-button
                    [label]="'COMMON.RESTRICTED.GENERATE_KEY' | translate"
                    icon="pi pi-key"
                    severity="secondary"
                    size="small"
                    (onClick)="goToGenerateKey()"
                />
            </div>
        </div>

        <app-key-unlock-dialog [(visible)]="unlockDialogVisible" />
    `,
    styles: [`
        .restricted-banner {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;
            gap: 0.75rem;
            padding: 0.75rem 1.25rem;
            margin: 0.75rem;
            border-radius: 8px;
            background: rgba(234, 179, 8, 0.12);
            border: 1px solid rgba(234, 179, 8, 0.55);
            color: #713f12;
        }
        :host-context(.app-dark) .restricted-banner,
        :host-context([class*='app-dark']) .restricted-banner {
            background: rgba(234, 179, 8, 0.14);
            color: #fde68a;
        }
        .restricted-banner-message {
            display: flex;
            align-items: center;
            gap: 0.6rem;
            font-size: 0.9rem;
        }
        .restricted-banner-message i {
            font-size: 1.1rem;
            color: #ca8a04;
        }
        .restricted-banner-actions {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            flex-wrap: wrap;
        }
    `]
})
export class RestrictedBannerComponent {
    private authService = inject(AuthService);
    private webBridge = inject(WebTokenBridge);
    private router = inject(Router);

    unlockDialogVisible = false;
    private keyPresent = this.authService.hasPrivateKey();

    constructor() {
        // Reaktif kilit durumu: anahtar yüklenince (dialog/login) banner anında kaybolsun.
        this.authService.privateKeyPresent$
            .pipe(takeUntilDestroyed())
            .subscribe(present => { this.keyPresent = present; });
    }

    showBanner(): boolean {
        // ENT4: QR (web köprüsü) oturumunda kişisel anahtar kilidi yok; ent anahtarı onay zarfından gelir.
        if (this.webBridge.active()) return false;
        return this.authService.hasValidTokens() && !this.keyPresent;
    }

    openUnlockDialog() {
        this.unlockDialogVisible = true;
    }

    goToGenerateKey() {
        this.router.navigate(['/info/generatekey']);
    }
}
